import React from "react";
import {
  Box,
  Button,
  Grid,
  makeStyles,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@material-ui/core";
import { useForm, Controller } from "react-hook-form";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "ckeditor5-custom-build/build/ckeditor";
import { KeyboardDateTimePicker } from "@material-ui/pickers";
import MovieSections from "../MovieSections";
import PeriodTable from "../PeriodTable";
import apiHelper from "../../../../util/ApiHelper";
import produce from "immer";
import _ from "lodash";
import { toast } from "react-toastify";
import { formatDateTimeWithTz, modal } from "../../../../util";
import editorConfiguration from "../../../../util/EditorConfiguration";
import dayjs from "dayjs";

// import ScreeningSections from "../ScreeningSections";

const frontendUrl = process.env.REACT_APP_CINEFAN_WEB_HOSTNAME;

const useStyles = makeStyles((theme) => ({
  tabpanel: {
    background: "#FFFFFF",
  },
  textInput: {
    backgroundColor: "white",
    width: "100%",
    marginBottom: "12px",

    "& input": {
      fontSize: "14px",
    },
  },
  content: {
    "& .ck-editor__editable_inline": {
      minHeight: "200px",
    },
  },
  buttonGroup: {
    marginTop: "20px",
    textAlign: "right",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  box: {
    padding: 0,
  },
  innerBox: {
    background: "#ffffff",
    padding: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  imageGallery: {
    border: "1px solid #CCCCCC",
    minHeight: "160px",
    borderRadius: "3px",
    position: "relative",
  },
  imageGalleryControl: {
    position: "relative",
    width: "100%",
    display: "flex",
    height: "40px",
    bottom: "0",
    borderTop: "1px solid #CCCCCC",
    padding: "5px",
  },
  imageGalleryBlk: {
    minHeight: "120px",
    padding: "5px",
  },
  imgPreview: {
    maxWidth: "200px",
    margin: "10px",
    display: "inline-flex",
    "& img": {
      width: "100%",
      height: "auto",
    },
  },
  permalink: {
    paddingLeft: "5px",
    fontSize: "13px",
    "& .permalink": {
      fontWeight: 700,
    },
  },
}));

function TabPanel(props) {
  const classes = useStyles();
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`nav-tabpanel-${index}`}
      aria-labelledby={`nav-tab-${index}`}
      {...other}
      // className={classes.tabpanel}
    >
      <Box p={3} className={classes.box}>
        {children}
      </Box>
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `nav-tab-${index}`,
    "aria-controls": `nav-tabpanel-${index}`,
  };
}

const defaultFormValues = {
  localizations: {
    en: {
      name: "",
      slug: "",
      title: "",
      description: "",
      orgTitle: "",
      director: "",
      cast: "",
      year: "",
      country: "",
      runtime: "",
      fileFormat: "",
      filmStyle: "",
      note: "",
      filmWebPage: "",
    },
    tc: {
      name: "",
      slug: "",
      title: "",
      description: "",
      orgTitle: "",
      director: "",
      cast: "",
      year: "",
      country: "",
      runtime: "",
      fileFormat: "",
      filmStyle: "",
      note: "",
      filmWebPage: "",
    },
  },
  movieList: [
    {
      movie: "",
      order: "",
    },
  ],
  tags: [],
  periodId: null,
};

export default function EditProgrammePage({ disabled, ...props }) {
  const { match } = props;
  const classes = useStyles();
  const [language, setLanguage] = React.useState(0);
  const [activeLang, setActiveLang] = React.useState("en");
  const [defaultValues, setDefaultValues] = React.useState(defaultFormValues);
  const [periodList, setPeriodList] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [fieldsSnapshot, setFieldsSnapshot] = React.useState([]);
  const [formError, setFormError] = React.useState({});

  // keep in case
  // const [clonedEnPermalink, setClonedEnPermalink] = React.useState("");
  // const [clonedTcPermalink, setClonedTcPermalink] = React.useState("");
  // const [isEnSlugInvalid, setIsEnSlugInvalid] = React.useState(false);
  // const [isTcSlugInvalid, setIsTcSlugInvalid] = React.useState(false);
  // const [isEditPermalink, setEditPermalink] = React.useState({
  //   en: false,
  //   tc: false,
  // });

  const [clonedPermalink, setClonedPermalink] = React.useState("");
  const [isSlugInvalid, setIsSlugInvalid] = React.useState(false);
  const [isEditPermalink, setEditPermalink] = React.useState(false);

  const {
    control,
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    reset,
    formState,
  } = useForm({
    defaultValues,
  });

  const periodId = watch("periodId");
  const movieList = watch("movieList");

  const getAllPeriod = async () => {
    try {
      const reuslt = await apiHelper.cinefan.getPeriodList();
      console.log(reuslt.data.content);
      const periodOptions = [];
      reuslt.data.content.forEach((o) => {
        o.childPeriod.forEach((childPeriod) => {
          const newObj = {
            id: childPeriod.id,
            period: childPeriod.localizations.en.name,
            year: o.localizations.en.name,
            slug: childPeriod.slug,
          };
          periodOptions.push(newObj);
        });
      });
      setPeriodList(periodOptions);
    } catch (err) {
      console.log(err);
    }
  };

  const getMovieById = async (id) => {
    const result = await apiHelper.cinefan.getMovieById(id);
    return result.data;
  };

  const getProgrammeDetail = React.useCallback(async () => {
    try {
      const result = await apiHelper.cinefan.getProgrammeById(match.params.id);
      return result.data;
    } catch (err) {
      console.log(err);
    }
  }, [match.params.id]);

  const prepareProgrammeData = React.useCallback(async () => {
    const programmeData = await getProgrammeDetail();
    const newProgrammeData = Object.assign({}, programmeData);
    newProgrammeData.movieList = newProgrammeData.movieList.map((movie) => {
      const detailMovieInfo = getMovieById(movie.movieId).then((result) => {
        console.log({ ...result, order: parseInt(movie.order) });
        return { ...result, order: parseInt(movie.order) };
      });
      return detailMovieInfo;
    });
    Promise.all(newProgrammeData.movieList).then((result) => {
      newProgrammeData.movieList = result;
      newProgrammeData.movieList = newProgrammeData.movieList.map((movie) => {
        return {
          id: movie.id,
          title: movie.localizations[activeLang].title,
          slug: movie.localizations.en.slug,
          order: parseInt(movie.order),
        };
      });
      setDefaultValues(newProgrammeData);
      setClonedPermalink(newProgrammeData.localizations.en.slug);
      reset(newProgrammeData);
    });
    // }
  }, [getProgrammeDetail, reset, activeLang]);

  const handleChange = (event, newValue) => {
    setLanguage(newValue);
  };

  const onSubmit = (articleStatus) => async (value) => {
    let data = value;
    data = produce(data, (draft) => {
      draft.movieList = draft.movieList.map((o) => {
        return { movieId: o.id, order: o.order };
      });
      //post immediately if no publish date
      draft.publishDate = formatDateTimeWithTz(dayjs(data.publishDate).isValid() ? data.publishDate : new Date());
      return draft;
    });
    console.log(data);
    if (!data.localizations.en.slug || !data.localizations.tc.slug) {
      toast.error("Slug required.");
      return;
    }
    try {
      const result = await apiHelper.cinefan.updpateProgrammeById(
        match.params.id,
        data
      );
      console.log(result);
      if (result.data.code === 0) {
        toast.dark("Your changes is saved.");
        prepareProgrammeData();
      }
    } catch (err) {
      console.log(err);
    }
  };

  const deleteProgramme = async (id) => {
    const decision = await modal.fire({
      title: "Delete Programme",
      text: "Are you sure to delete this programme?",
      showCancelButton: true,
      confirmButtonText: `Delete`,
    });
    if (decision.isConfirmed) {
      try {
        const result = await apiHelper.cinefan.deleteProgrammeById(id);
        console.log(result);
      } catch (err) {
        console.log(err);
      }
    }
  };

  React.useEffect(() => {
    getAllPeriod();
  }, []);

  React.useEffect(() => {
    prepareProgrammeData();
  }, [prepareProgrammeData]);

  React.useEffect(() => {
    if (loading && !_.isEqual(fieldsSnapshot, movieList)) {
      //Forced update 2nd time to ensure the data is really updated
      setValue("movieList", fieldsSnapshot);
    }
    setLoading(false);
  }, [loading]);

  React.useEffect(() => {
    console.log(formError);
    if (!_.isEqual(formError, formState.errors)) {
      setFormError(formState.errors);
      if (formState.errors?.localizations?.en) {
        setLanguage(0);
        setActiveLang("en");
        return;
      }
      if (formState.errors?.localizations?.tc) {
        setLanguage(1);
        setActiveLang("tc");
        return;
      }
    }
  }, [formState, formError]);

  const selectedRows = React.useMemo(() => {
    let obj = null;
    if (periodId && periodList.length > 0) {
      obj = {};
      console.log({ periodId, periodList });
      const targetIndex = _.findIndex(periodList, ["id", periodId]);
      obj[targetIndex] = true;
    }
    return obj;
  }, [periodList, periodId]);

  return (
    <div className={classes.content}>
      <Typography variant="h4" gutterBottom>
        Programmes & Periods > Edit Programme
      </Typography>
      <Tabs
        value={language}
        onChange={handleChange}
        aria-label="nav tabs example"
        TabIndicatorProps={{ style: { top: "0", background: "#306F9D" } }}
      >
        <Tab
          label="English"
          {...a11yProps(0)}
          onClick={() => setActiveLang("en")}
        />
        <Tab
          label="繁體中文"
          {...a11yProps(1)}
          onClick={() => setActiveLang("tc")}
        />
      </Tabs>
      <form>
        <fieldset disabled={disabled} className="fieldset">
          <TabPanel value={language} index={0}>
            <Box className={classes.innerBox}>
              <Grid container direction="vertical" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.en.name"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Programme Name"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        fullWidth
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <div className={classes.permalink}>
                    <span className="permalink">Permalink: </span>
                    {!isEditPermalink && (
                      <>
                        {frontendUrl || "https://cinefan.com.hk"}/programme/
                        {getValues(`localizations.en.slug`)
                          ? getValues(`localizations.en.slug`)
                          : defaultValues &&
                            defaultValues.localizations.en.slug}
                        <Button
                          variant="outlined"
                          disabled={disabled}
                          onClick={() => {
                            setEditPermalink( true );
                            setClonedPermalink(
                              getValues(`localizations.en.slug`)
                                ? getValues(`localizations.en.slug`)
                                : defaultValues &&
                                    defaultValues.localizations.en.slug
                            );
                          }}
                        >
                          Edit
                        </Button>
                      </>
                    )}
                    {isEditPermalink && (
                      <>
                        <span>
                          {frontendUrl || "https://cinefan.com.hk"}/programme/
                        </span>
                        <Controller
                          name={`localizations.en.slug`}
                          control={control}
                          defaultValue={
                            defaultValues && defaultValues.localizations.en.slug
                          }
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              variant="outlined"
                              margin="dense"
                              {...field}
                              error={isSlugInvalid}
                              helperText={isSlugInvalid && "Slug required"}
                              onChange={(e) => {
                                field.onChange(e);
                                setValue(
                                  "localizations.en.slug",
                                  e.target.value
                                );
                                setValue(
                                  "localizations.tc.slug",
                                  e.target.value
                                );
                              }}
                            />
                          )}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => {
                            if (getValues(`localizations.en.slug`)) {
                              setIsSlugInvalid(false);
                              setEditPermalink(
                                false
                              );
                            } else {
                              setIsSlugInvalid(true);
                            }
                          }}
                        >
                          OK
                        </Button>
                        <Button
                          variant="text"
                          onClick={() => {
                            setValue(
                              `localizations.en.slug`,
                              clonedPermalink
                            );
                            setEditPermalink( false );
                            setIsSlugInvalid(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.en.description"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                        disabled={disabled}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
          <TabPanel value={language} index={1}>
            <Box className={classes.innerBox}>
              <Grid container direction="vertical" spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name={`localizations.tc.name`}
                    control={control}
                    defaultValue=""
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        label="Programme Name"
                        variant="outlined"
                        margin="dense"
                        className={classes.textInput}
                        fullWidth
                        {...field}
                        error={!!error}
                        helperText={error ? error.message : ""}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <div className={classes.permalink}>
                    <span className="permalink">Permalink: </span>
                    {!isEditPermalink && (
                      <>
                        {frontendUrl || "https://cinefan.com.hk"}/zh/programme/
                        {getValues(`localizations.en.slug`)
                          ? getValues(`localizations.en.slug`)
                          : defaultValues &&
                            defaultValues.localizations.en.slug}
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setEditPermalink( true );
                            setClonedPermalink(
                              getValues(`localizations.en.slug`)
                                ? getValues(`localizations.en.slug`)
                                : defaultValues &&
                                    defaultValues.localizations.en.slug
                            );
                          }}
                        >
                          Edit
                        </Button>
                      </>
                    )}
                    {isEditPermalink && (
                      <>
                        <span>
                          {frontendUrl || "https://cinefan.com.hk"}/zh/programme/
                        </span>
                        <Controller
                          name={`localizations.en.slug`}
                          control={control}
                          defaultValue={defaultFormValues.localizations.en.slug}
                          render={({ field, fieldState: { error } }) => (
                            <TextField
                              variant="outlined"
                              margin="dense"
                              {...field}
                              error={isSlugInvalid}
                              helperText={isSlugInvalid && "Slug required"}
                              onChange={(e) => {
                                field.onChange(e);
                                setValue(
                                  "localizations.en.slug",
                                  e.target.value
                                );
                                setValue(
                                  "localizations.tc.slug",
                                  e.target.value
                                );
                              }}
                            />
                          )}
                        />
                        <Button
                          variant="outlined"
                          onClick={() => {
                            if (getValues(`localizations.en.slug`)) {
                              setIsSlugInvalid(false);
                              setEditPermalink(false);
                            } else {
                              setIsSlugInvalid(true);
                            }
                          }}
                        >
                          OK
                        </Button>
                        <Button
                          variant="text"
                          onClick={() => {
                            setValue(
                              `localizations.en.slug`,
                              clonedPermalink
                            );
                            setEditPermalink(false);
                          }}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="localizations.tc.description"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <CKEditor
                        editor={Editor}
                        config={editorConfiguration}
                        data={field.value}
                        // onReady={(editor) => {
                        //   // You can store the "editor" and use when it is needed.
                        //   console.log("Editor is ready to use!", editor);
                        // }}
                        onChange={(event, editor) => {
                          const data = editor.getData();
                          field.onChange(data);
                        }}
                        disabled={disabled}
                        // onBlur={(event, editor) => {
                        //   console.log("Blur.", editor);
                        // }}
                        // onFocus={(event, editor) => {
                        //   console.log("Focus.", editor);
                        // }}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          <Box className={classes.innerBox}>
            <Grid container xs={12} direction="column" spacing={3}>
              <Grid container item xs={12} spacing={2} direction="column">
                <Grid item xs={12}>
                  <Typography variant="span">Period</Typography>
                </Grid>
                <Grid item xs={12}>
                  {selectedRows && (
                    <PeriodTable
                      periodList={periodList}
                      control={control}
                      language={activeLang}
                      periodId={periodId}
                      selectedRows={selectedRows}
                      setValue={setValue}
                      getValues={getValues}
                      register={register}
                    />
                  )}
                </Grid>
              </Grid>
              <Grid container item xs={12} spacing={2} direction="column">
                <Grid item xs={12}>
                  <Typography variant="span">Programme Movie List</Typography>
                </Grid>
                <Grid item xs={12}>
                  {loading ? (
                    <p>loading...</p>
                  ) : (
                    <MovieSections
                      setValue={setValue}
                      getValues={getValues}
                      control={control}
                      language={activeLang}
                      setLoading={setLoading}
                      setFieldsSnapshot={setFieldsSnapshot}
                      disabled={disabled}
                    />
                  )}
                </Grid>
              </Grid>
            </Grid>
          </Box>
          <Box className={classes.innerBox}>
            <Grid container xs={12} direction="column" spacing={3}>
              <Grid container item xs={12}>
                <Typography variant="span">Publish Settings</Typography>
              </Grid>
              <Grid container item xs={12}>
                <Grid container item xs={6} spacing={3}>
                  <Grid item xs={12}>
                    <Controller
                      name={`publishDate`}
                      control={control}
                      render={({ field: { value, onChange, ...rest }, fieldState: { error } }) => (
                        <KeyboardDateTimePicker
                          fullWidth
                          variant="inline"
                          format="yyyy-MM-dd HH:mm:ss"
                          margin="dense"
                          id="date-picker-inline"
                          label="Date time"
                          {...rest}
                          value={value}
                          onChange={(date, val) => onChange(val)}
                          disabled={disabled}
                          InputLabelProps={{
                            shrink: true,
                          }}
                          KeyboardButtonProps={{
                            "aria-label": "change date",
                          }}
                          helperText={
                            "Your post will be posted immediately if you don’t set a custom date and time."
                          }
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
          {disabled || (
            <div className={classes.buttonGroup}>
              <Button
                type="button"
                variant="contained"
                disableElevation
                style={{
                  color: "white",
                  background: "#306F9D",
                  marginRight: "5px",
                  padding: "9px 16px",
                }}
                onClick={handleSubmit(onSubmit())}
              >
                Save
              </Button>

              <Button
                type="button"
                variant="contained"
                disableElevation
                style={{
                  color: "white",
                  background: "#962013",
                  padding: "9px 16px",
                }}
                onClick={() => deleteProgramme(match.params.id)}
              >
                Delete
              </Button>
            </div>
          )}
        </fieldset>
      </form>
    </div>
  );
}
